/**
 * The moves — every game intent, as one thin call each.
 *
 * Split out of `gameLink.ts` when it reached 752 lines, along the seam the file
 * already had: above it is the **connection** (the socket, the reconnect, the
 * heartbeat, the health machine); this is the list of intents, and there is no
 * state between them. Each takes the `GameLink` the connection handed back and
 * returns what the server said.
 *
 * **Nothing here decides anything.** The engine is the authority on whether a
 * move is legal, so a refusal is a normal answer rather than an exception to
 * pre-empt — which is why these are one line apiece and why a screen may call
 * one it expects to be refused.
 *
 * ⚠️ Imported through the `services/gameLink.js` barrel, not directly. A
 * component may not import this at all — see the eslint boundary in
 * `web/eslint.config.ts`, which lists this directory explicitly because
 * `**\/services/*` does not match a path one level deeper.
 */

import type {
  GameAck,
  RateAck,
  ShopItem,
  VoteReason,
  Watching,
} from "../generated/client.js";
import type { GameLink } from "./types.js";

/**
 * Ask the actor to say where the game is, again.
 *
 * `watch` is idempotent and republishes, which makes it the reconnect path as
 * well as the first-connect one. Calling it after a socket drops is how a phone
 * coming back from a lock screen catches up.
 */
export async function refresh(link: GameLink): Promise<Watching> {
  return link.client.game.watch(undefined);
}

/** Start the game. Host only; anybody else is refused server-side. */
export async function startGame(link: GameLink): Promise<GameAck> {
  return link.client.game.start(undefined);
}

/**
 * Deal the next card and start the clip.
 *
 * The one intent that names nobody — a round belongs to the table. The engine
 * refuses it anywhere but `turn_ready`, so a double tap is a rejection rather
 * than two cards.
 */
export async function deal(link: GameLink): Promise<GameAck> {
  return link.client.game.deal(undefined);
}

/** Choose options. Either grid may be left alone. */
export async function guess(
  link: GameLink,
  artistOption: number | null,
  titleOption: number | null,
): Promise<GameAck> {
  return link.client.game.guess({
    artist_option: artistOption,
    title_option: titleOption,
  });
}

/** Done guessing; move on to the timeline. */
export async function lockGuess(link: GameLink): Promise<GameAck> {
  return link.client.game.lock_guess(undefined);
}

/** Place the card. `exactYear` is the bonus, and being wrong costs nothing. */
export async function place(
  link: GameLink,
  slot: number,
  exactYear: number | null,
): Promise<GameAck> {
  return link.client.game.place({ slot, exact_year: exactYear });
}

/**
 * Buy your team another vote — phase 20h-3, ADR 0029.
 *
 * Only on a genuine deadlock: everybody voted and no slot won. Charges the
 * side's purse — which is the team's shared bank — and clears the members'
 * slots so they choose again. ⚠️ The grids are left alone: a deadlock is about
 * *where the card goes*, and re-asking the artist would charge a team for a
 * disagreement they did not have.
 */
export async function revote(link: GameLink): Promise<GameAck> {
  return link.client.game.revote();
}

/**
 * Answer *close the party, or keep waiting* — phase 21, **ADR 0030**.
 *
 * A party stalls when nobody who may move it on is still here. After a grace
 * period the room is asked, and this is one person's answer; `true` closes it.
 *
 * ⚠️ **Only while the vote window is open.** The grace period before it is not a
 * window anybody may act in — the engine refuses an early answer, because
 * otherwise one player could open the question by answering it.
 *
 * An answer may be changed while the window is open; the last one counts.
 */
export async function stallVote(link: GameLink, close: boolean): Promise<GameAck> {
  return link.client.game.stall_vote({ close });
}

/** Buy a power that needs no target. */
export async function purchase(link: GameLink, item: ShopItem): Promise<GameAck> {
  return link.client.game.purchase({ item });
}

/**
 * Contest the active side's placement, naming the slot you claim is right.
 *
 * One per side per round, and **one claim per slot** — a gap somebody else
 * claimed, or the one the active side claimed, is refused (`slot_taken`) and
 * costs nothing.
 *
 * 🔴 **Since 20h-1 it carries an answer** (ADR 0029): the same grids the active
 * player had, and a year if you want one. The **slot** is the only part that
 * wins the card; the rest pays coins, and only if the slot was right — naming
 * the artist while pointing at the wrong gap earns nothing at all.
 */
export async function challenge(
  link: GameLink,
  slot: number,
  answer: {
    artistOption: number | null;
    titleOption: number | null;
    exactYear: number | null;
  },
): Promise<GameAck> {
  return link.client.game.challenge({
    slot,
    artist_option: answer.artistOption,
    title_option: answer.titleOption,
    exact_year: answer.exactYear,
  });
}

/**
 * Decline to challenge — phase 16b.
 *
 * The other half of the ready check: the window closes when every eligible side
 * has answered, so the active player is told **nobody objected** rather than
 * **time ran out**. Free and idempotent.
 */
export async function passChallenge(link: GameLink): Promise<GameAck> {
  return link.client.game.pass_challenge();
}

/**
 * Take a placed card off another side.
 *
 * ⚠️ **Two beats since phase 18, finding #24.** This no longer moves a card: it
 * opens a window in which the side being robbed may pay to block. The card moves
 * when the window closes — blocked, declined, or timed out.
 */
export async function steal(
  link: GameLink,
  from: string,
  track: string,
): Promise<GameAck> {
  return link.client.game.steal({ from, track });
}

/**
 * Pay to block the steal aimed at your side — phase 18, finding #24.
 *
 * The game's only reactive purchase. Takes no arguments: there is only ever one
 * steal open, and the server is the authority on who is being robbed.
 */
export async function defendSteal(link: GameLink): Promise<GameAck> {
  return link.client.game.defend_steal();
}

/**
 * Let the steal through rather than paying to stop it.
 *
 * The other half of the ready check, exactly as `passChallenge` is for the
 * challenge window. Free. ⚠️ Absent counts as declined, so a phone that never
 * answers reaches the same outcome.
 */
export async function allowSteal(link: GameLink): Promise<GameAck> {
  return link.client.game.allow_steal();
}

/**
 * Say the year on screen is wrong (ADR 0009).
 *
 * **Free, and it is a bug report rather than a move.** The suggested year is a
 * hint for the catalog's re-check and is never applied as a value. Everybody can
 * press it, including players who were not up this round — the person who knows
 * the song is not necessarily the person whose turn it was.
 */
export async function flag(
  link: GameLink,
  suggestedYear: number | null,
): Promise<GameAck> {
  return link.client.game.flag({ suggested_year: suggestedYear });
}

/**
 * Say what you thought of the card itself (phase 13).
 *
 * **A different question from [`flag`], and the difference matters.** A flag
 * says *the year is wrong* and can void the round; this says *that was a bad
 * card* — the audio is not the song, the clip is broken, nobody could have known
 * it — and changes nothing about the game. It is free, it is one vote per person
 * per track however many times it is pressed, and enough parties saying *off*
 * takes the card out of the deal for everybody.
 *
 * `recorded: false` means there was no card in flight — a phone that was a
 * moment late, which is not an error.
 */
export async function rateCard(
  link: GameLink,
  keep: boolean,
  reason: string | null,
  reasonCode: VoteReason | null = null,
): Promise<RateAck> {
  return link.client.game.rate({ keep, reason, reason_code: reasonCode });
}

/** Leave `Settled` and start the next round. */
export async function advance(link: GameLink): Promise<GameAck> {
  return link.client.game.advance(undefined);
}
